import React, { useEffect, useState } from 'react';
import { Download, Trash2, Search, Mail } from 'lucide-react';
import { api, API_BASE } from '../../lib/api';

export default function LeadsInbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/leads');
      setItems(data?.items || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar este lead?')) return;
    await api.delete(`/admin/leads/${id}`);
    if (selected?.id === id) setSelected(null);
    load();
  };

  const filtered = items.filter(it => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [it.name, it.email, it.company, it.role, it.use_case, it.message]
      .filter(Boolean).some(v => String(v).toLowerCase().includes(s));
  });

  return (
    <div data-testid="leads-page">
      <div className="flex items-start justify-between mb-8 pb-6 border-b border-line gap-4 flex-wrap">
        <div>
          <p className="text-eyebrow text-ink-muted mb-2">Bandeja</p>
          <h1 className="font-display text-4xl md:text-5xl text-ink">Leads recibidos</h1>
        </div>
        <a
          href={`${API_BASE}/api/admin/leads/export.csv`}
          target="_blank"
          rel="noreferrer"
          className="btn-ghost !px-5 !py-2.5 !text-[11px]"
          data-testid="leads-export"
        >
          <Download size={13} /> Exportar CSV
        </a>
      </div>

      <div className="mb-5 relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
        <input
          type="search"
          placeholder="Buscar por nombre, email, empresa…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full pl-10 pr-3 py-2.5 bg-bg-secondary border border-line rounded-md text-sm font-mono text-ink placeholder:text-ink-dim focus:border-acid outline-none"
          data-testid="leads-search"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3 bg-bg-secondary border border-line rounded-md overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-ink-muted">Cargando…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-ink-muted">
              <Mail size={32} className="mx-auto mb-3 text-ink-dim" />
              {items.length === 0 ? 'Aún no hay leads.' : 'Sin resultados.'}
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="leads-table">
              <thead className="bg-bg border-b border-line">
                <tr className="text-left text-eyebrow text-ink-muted">
                  <th className="px-4 py-3 font-medium">Fecha</th>
                  <th className="px-4 py-3 font-medium">Nombre</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Empresa</th>
                  <th className="px-4 py-3 font-medium w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr
                    key={l.id}
                    onClick={() => setSelected(l)}
                    className={`border-b border-line cursor-pointer transition-colors ${selected?.id === l.id ? 'bg-acid/5' : 'hover:bg-bg-surface'}`}
                    data-testid={`lead-row-${l.id}`}
                  >
                    <td className="px-4 py-3 text-ink-muted text-xs whitespace-nowrap">
                      {l.created_at?.slice(0, 16).replace('T', ' ')}
                    </td>
                    <td className="px-4 py-3 text-ink">
                      <div className="font-medium">{l.name}</div>
                      <div className="text-xs text-ink-muted truncate max-w-[18ch]">{l.email}</div>
                    </td>
                    <td className="px-4 py-3 text-ink-muted hidden md:table-cell">{l.company || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); remove(l.id); }}
                        className="text-ink-muted hover:text-danger"
                        data-testid={`lead-delete-${l.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <div className="bg-bg-secondary border border-line rounded-md p-6 sticky top-6" data-testid="lead-detail">
              <div className="text-eyebrow text-ink-muted mb-1">{selected.created_at?.slice(0, 16).replace('T', ' ')}</div>
              <h2 className="font-display text-3xl text-ink mb-1">{selected.name}</h2>
              <a href={`mailto:${selected.email}`} className="text-acid font-mono text-sm">{selected.email}</a>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-6 pt-6 border-t border-line">
                {[
                  ['Empresa', selected.company],
                  ['Cargo', selected.role],
                  ['Idioma', selected.locale],
                  ['IP', selected.ip],
                ].map(([k, v]) => v && (
                  <div key={k}>
                    <div className="text-eyebrow text-ink-dim mb-1">{k}</div>
                    <div className="text-sm text-ink truncate">{v}</div>
                  </div>
                ))}
              </div>
              {selected.use_case && (
                <div className="mt-5 pt-5 border-t border-line">
                  <div className="text-eyebrow text-ink-dim mb-1">Caso de uso</div>
                  <div className="text-sm text-ink whitespace-pre-wrap">{selected.use_case}</div>
                </div>
              )}
              {selected.message && (
                <div className="mt-5 pt-5 border-t border-line">
                  <div className="text-eyebrow text-ink-dim mb-1">Mensaje</div>
                  <div className="text-sm text-ink whitespace-pre-wrap">{selected.message}</div>
                </div>
              )}
              <div className="mt-6 flex gap-2">
                <a href={`mailto:${selected.email}`} className="btn-acid !px-4 !py-2 !text-[10px]">
                  <Mail size={12} /> Responder
                </a>
                <button onClick={() => remove(selected.id)} className="btn-ghost !px-4 !py-2 !text-[10px] hover:!text-danger hover:!border-danger">
                  <Trash2 size={12} /> Eliminar
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-bg-secondary/40 border border-dashed border-line rounded-md p-10 text-center text-ink-muted text-sm" data-testid="lead-empty">
              Selecciona un lead para ver el detalle
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
